/**
 * Attack-speed auras (opal / black opal). The prototype rescanned the whole
 * grid inside every cooldown update; the sources are collected once per tick
 * here instead. Exact-semantics optimization: same row-major visit order, same
 * strict `rate > best` tie-break (first-best wins), same integer distances --
 * locked by aura.test.ts against a naive full-scan reference.
 */
import { GEMS, NX, NY, SPECIALS } from '../data/balance.js';
import { dist } from './dist.js';
import { prismMult, type SlateInstance } from './slates.js';
import type { Cell, GemCell } from './types.js';

export interface AuraSource {
  readonly x: number;
  readonly y: number;
  readonly rate: number;
  readonly range: number;
}

/**
 * Every settled aura gem on the board, in row-major order. A Prism-family
 * slate covering the source multiplies its emitted rate (ADR 0008); with no
 * slates the rates are untouched, keeping pre-slate replays bit-exact.
 */
export function collectAuraSources(
  grid: readonly (readonly (Cell | null)[])[],
  slates: readonly SlateInstance[] = [],
): AuraSource[] {
  const sources: AuraSource[] = [];
  for (let y = 0; y < NY; y++) {
    for (let x = 0; x < NX; x++) {
      const g = grid[y]![x];
      if (!g || g.kind !== 'gem' || g.isNew) continue;
      let source: AuraSource | null = null;
      if (g.special === 'blackopal') {
        source = { x, y, rate: SPECIALS.blackopal.aura!, range: SPECIALS.blackopal.range };
      } else if (g.special === undefined && g.type === 'opal') {
        source = { x, y, rate: 0.15 + g.tier * 0.09, range: GEMS.opal.range + g.tier * 0.2 };
      }
      if (!source) continue;
      if (slates.length > 0) {
        const mult = prismMult(slates, x, y);
        if (mult > 1) source = { ...source, rate: source.rate * mult };
      }
      sources.push(source);
    }
  }
  return sources;
}

/** Best (not summed) aura rate affecting `cell`; a gem never buffs itself. */
export function auraBonusFor(sources: readonly AuraSource[], cell: GemCell): number {
  let best = 0;
  for (const s of sources) {
    if (s.x === cell.x && s.y === cell.y) continue;
    const d = dist(s.x - cell.x, s.y - cell.y);
    if (d <= s.range && s.rate > best) best = s.rate;
  }
  return best;
}
